import { Injectable, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Structural shape of a portal directive — avoids importing SearchPortalEntry here
 * (which would create a circular dependency: directive imports registry).
 */
export interface SearchPortalEntry {
  searchPortal: string;
  viewContainerRef: ViewContainerRef;
}

/**
 * Global registry of `SearchPortalEntry` instances keyed by their `searchPortal` name.
 *
 * Lets `campus-search` discover portal anchors declared anywhere in the application
 * — not only within its own template tree. Use this when the portal anchor lives in a
 * sibling routed component (e.g. a page-header) while `<campus-search>` lives in the
 * content component.
 *
 * Backwards-compatible: existing call sites that pass `[searchPortals]` to
 * `<campus-search>` keep working; the input portals are merged with the registry portals.
 */
@Injectable({ providedIn: 'root' })
export class SearchPortalsRegistry {
  private readonly _portals$ = new BehaviorSubject<Map<string, SearchPortalEntry>>(new Map());

  /** All currently registered portals, emits whenever the registry changes. */
  readonly portals$: Observable<Map<string, SearchPortalEntry>> = this._portals$.asObservable();

  /** Convenience selector returning the directives as an array. */
  readonly portalsArray$: Observable<SearchPortalEntry[]> = this.portals$.pipe(
    map((map) => Array.from(map.values()))
  );

  register(portal: SearchPortalEntry): void {
    if (!portal?.searchPortal) return;
    const next = new Map(this._portals$.value);
    next.set(portal.searchPortal, portal);
    this._portals$.next(next);
  }

  unregister(portal: SearchPortalEntry): void {
    if (!portal?.searchPortal) return;
    const next = new Map(this._portals$.value);
    if (next.get(portal.searchPortal) !== portal) return;
    next.delete(portal.searchPortal);
    this._portals$.next(next);
  }

  /** Snapshot lookup (non-reactive). */
  get(name: string): SearchPortalEntry | undefined {
    return this._portals$.value.get(name);
  }

  /** Snapshot count (non-reactive). */
  get size(): number {
    return this._portals$.value.size;
  }
}
